import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const INTERRUPT_EVENTS = ['wheel', 'touchmove', 'keydown'];
const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ']);

// Scrolls to whichever section matches location.hash, on mount and whenever
// the hash changes — nav links use plain #id hashes (see TopBar's section
// links and WorldPage's own Hero CTAs) so this works whether WorldPage was
// already mounted or just navigated to.
function useScrollToHash() {
    const { hash, key } = useLocation();

    useEffect(() => {
        if (!hash) return undefined;

        const targetId = hash.slice(1);
        let userInterrupted = false;

        function scrollToTarget(behavior) {
            document.getElementById(targetId)?.scrollIntoView({ behavior });
        }

        scrollToTarget('smooth');

        // Any of these is a direct signal the user is scrolling on purpose —
        // unlike the generic 'scroll' event (which also fires for our own
        // programmatic scrollIntoView calls below, making it useless for
        // telling the two apart), these only ever come from real input.
        function markUserInterrupted(event) {
            if (event.type === 'keydown' && !SCROLL_KEYS.has(event.key)) return;
            userInterrupted = true;
        }

        // 'auto' (instant), not 'smooth', for these corrections — content
        // above the target can resize several times in quick succession as
        // it loads in, and each 'smooth' call would cancel the previous
        // still-animating scroll and restart it, stuttering without ever
        // settling. Snapping instantly keeps the target glued under the
        // viewport through every resize; the initial scrollToTarget('smooth')
        // above is the only one the user actually sees animate.
        const resizeObserver = new ResizeObserver(() => {
            if (userInterrupted) {
                resizeObserver.disconnect();
                return;
            }
            scrollToTarget('auto');
        });
        resizeObserver.observe(document.body);
        const stopWatching = setTimeout(() => resizeObserver.disconnect(), 5000);

        INTERRUPT_EVENTS.forEach((event) => window.addEventListener(event, markUserInterrupted, { passive: true }));

        return () => {
            resizeObserver.disconnect();
            clearTimeout(stopWatching);
            INTERRUPT_EVENTS.forEach((event) => window.removeEventListener(event, markUserInterrupted));
        };
    }, [hash, key]);
}

export default useScrollToHash;
